import React, { useState, useRef, useEffect } from 'react';
import UserProfile from './UserProfile';
import Sidebar from './Sidebar';
import ProductIntro from './ProductIntro';
import QuestionTester from './QuestionTester';
import { callSealSeekAPI } from '../services/qianwenAPI';
import {
  addChatHistoryItem,
  getChatHistory,
  getChatMessages,
  getCurrentChatTitle,
  saveChatMessages,
  saveCurrentChatTitle
} from '../services/chatStorage';
import './ChatDialog.css';

const buildChatTitle = (text) => {
  const content = String(text || '').trim();
  return content.length > 20 ? `${content.substring(0, 20)}...` : content;
};

const ChatDialog = ({ userInfo, onLogout }) => {
  const [messages, setMessages] = useState(() => {
    const cachedMessages = getChatMessages();
    return cachedMessages;
  });
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [profileShowContactInfo, setProfileShowContactInfo] = useState(false);
  const [profileContactOnly, setProfileContactOnly] = useState(false);
  const [showQuestionTester, setShowQuestionTester] = useState(false);
  const [showProductIntro, setShowProductIntro] = useState(() => {
    const cachedMessages = getChatMessages();
    return cachedMessages.length === 0;
  });
  const [currentChatTitle, setCurrentChatTitle] = useState(getCurrentChatTitle);
  const [historyItems, setHistoryItems] = useState(getChatHistory);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    saveChatMessages(messages);
  }, [messages]);

  useEffect(() => {
    saveCurrentChatTitle(currentChatTitle);
  }, [currentChatTitle]);

  const handleSend = async () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput || isLoading) return;

    // 如果是第一条用户消息，设置对话标题
    if (!currentChatTitle) {
      const title = buildChatTitle(trimmedInput);
      setCurrentChatTitle(title);
      setHistoryItems(addChatHistoryItem(title));
    }

    const userMessage = {
      id: Date.now(),
      text: trimmedInput,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setShowProductIntro(false); // 发送消息后隐藏产品介绍
    setIsLoading(true);

    try {
      const response = await callSealSeekAPI(inputValue);
      const aiMessage = {
        id: Date.now() + 1,
        text: response,
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      // 检测是否为内容安全相关错误，显示友好提示
      const errorMsg = error.message || '';
      const isContentError = 
        errorMsg.includes('inappropriate content') ||
        errorMsg.includes('inappropriate-content') ||
        errorMsg.includes('content security') ||
        errorMsg.includes('内容违规') ||
        errorMsg.includes('内容安全') ||
        errorMsg.includes('敏感内容');
      
      const errorMessage = {
        id: Date.now() + 1,
        text: isContentError 
          ? '抱歉！我只能回答电商行业问题。' 
          : `抱歉，我遇到了一些问题：${error.message || '未知错误'}。请稍后再试。`,
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      console.error('API调用错误:', error);
      console.error('错误详情:', {
        message: error.message,
        stack: error.stack
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const openUserProfile = (showContact = false, contactOnly = false) => {
    setProfileShowContactInfo(showContact);
    setProfileContactOnly(contactOnly);
    setShowUserProfile(true);
  };

  return (
    <div className="chat-dialog-container">
      <Sidebar
        onUserClick={() => openUserProfile(false, false)}
        onContactClick={() => openUserProfile(true, true)}
        onQuestionTestClick={() => setShowQuestionTester(true)}
        currentChatTitle={currentChatTitle}
        userInfo={userInfo}
        historyItems={historyItems}
      />
      
      <div className="chat-main">
        {currentChatTitle && (
          <div className="chat-header">
            <h1 className="chat-title">{currentChatTitle}</h1>
          </div>
        )}
        
        <div className="chat-messages">
          {showProductIntro && messages.length === 0 && (
            <ProductIntro onDismiss={() => setShowProductIntro(false)} />
          )}
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message ${message.sender === 'user' ? 'user-message' : 'ai-message'}`}
            >
              <div className="message-content">
                {message.text}
                {message.sender === 'ai' && (
                  <div className="message-ai-disclaimer">内容由AI生成，仅供参考</div>
                )}
              </div>
              <div className="message-time">
                {message.timestamp.toLocaleTimeString('zh-CN', { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="message ai-message">
              <div className="message-content loading">
                <span className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-container">
          <div className="input-row">
            <textarea
              ref={inputRef}
              className="chat-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="有问题,尽管问,Shift + Enter 换行"
              rows="1"
            />
            <button
              className="send-button"
              onClick={handleSend}
              disabled={!inputValue.trim() || isLoading}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <div className="input-actions">
            <button className="action-button">深度思考</button>
            <button className="action-button">联网搜索</button>
          </div>
          <div className="ai-disclaimer">内容由AI生成，仅供参考</div>
        </div>
      </div>

      {showUserProfile && (
        <UserProfile
          onClose={() => setShowUserProfile(false)}
          userInfo={userInfo}
          initialShowContactInfo={profileShowContactInfo}
          contactOnly={profileContactOnly}
          onLogout={onLogout}
        />
      )}

      {showQuestionTester && (
        <QuestionTester onClose={() => setShowQuestionTester(false)} />
      )}
    </div>
  );
};

export default ChatDialog;
